// The lead flow's analytics events: one builder per event, so names and
// properties are defined, and tested, in one place. Properties are small
// bounded scalars only: never an answer value, a name, a number or an id.
// Events from the lead form itself also carry `lead_type`, added where
// they are sent (see ./analytics).

import type { AnalyticsProperties } from "analytics/AnalyticsContext";

import type { CorrectionRefusalReason } from "pages/Login/correction-errors";
import type {
  CorrectionSaveOutcome,
  DestinationChannel,
} from "pages/Login/destination-edit";
import type { GrantPurpose } from "pages/Login/login-grant-flow";
import type { ResumeFailure, ResumeOutcome } from "pages/StartResume/resume";

import type { Answers } from "./answers";
import type { StepEntry } from "./entry";
import { leadKindOf, type LeadKind, type QuestionStepId } from "./flow";
import type { SignInFailure, SignInMode } from "./signIn";
import type { ConfirmRefusal } from "./submissionStatus";
import type { SubmitFailureReason, SubmitOutcome } from "./submit";

export type LeadFlowEvent = {
  name: string;
  properties: AnalyticsProperties;
};

export const UNKNOWN_LEAD_TYPE = "unknown";

/** The lead type the answers commit to, `unknown` before the first one. */
export function leadTypeOf(answers: Answers): LeadKind | "unknown" {
  return answers.situation === null ? UNKNOWN_LEAD_TYPE : leadKindOf(answers);
}

export type EndScreen = "done" | "out_of_area" | "other_options";

export type ConfirmMethod =
  "sms_otp" | "email_link" | "remembered_browser" | "signed_in";

/** The screen that hosts a code or link step: the lead form's sign-in panel,
 * the verify page's full-page code screen or its look-up, the quote's
 * re-entry, the account upgrade, or the sign-in page. */
export type ConfirmSurface =
  "start_panel" | "verify" | "verify_look_up" | "reentry" | "account" | "login";

/** The purpose a grant or link was made for, `unknown` when nothing says. */
export type EventPurpose = GrantPurpose | "unknown";
export type ConfirmSendMethod = "sms_otp" | "email_link";
export type ConfirmTrigger =
  "automatic" | "resend" | "switch_method" | "code_ended";

/** What a failed code or link step reports. The first six are the
 * server's answers about the grant; the rest are failures on the way. */
export type ConfirmFailureReason =
  | "grant_invalid"
  | "wrong_code"
  | "code_expired"
  | "rate_limited"
  | "cannot_sign_in"
  | "sms_unavailable"
  | "recaptcha_failed"
  | "sign_in_failed"
  | "network"
  | "other";

/** How a lead confirm ended: where it landed, or why it did not. */
export type ConfirmCompletedOutcome =
  ResumeOutcome | ResumeFailure | "incomplete";

/** The event as the lead form sends it, with the lead type it was sent
 * under. */
export function withLeadType(
  { name, properties }: LeadFlowEvent,
  leadType: string,
): LeadFlowEvent {
  return { name, properties: { ...properties, lead_type: leadType } };
}

/** The screen a sign-in or look-up runs on: the lead form's first screen,
 * the verify page, or the quote's re-entry. */
export type SignInSurface = "start" | "verify" | "reentry";

/** Where a session ended: the grant's look-up, a failed send, a failed
 * verify, or the emailed link as it opened. */
export type SessionEndedStage = "lookup" | "send" | "verify" | "link";

/** What the screen offers the visitor to go on: a replacement, the typed
 * look-up, or only a call. */
export type SessionEndedResolution = "renew" | "look_up" | "call";

export type SessionRenewalOutcome =
  "minted" | "confirmed" | "unavailable" | "rate_limited" | "failed";

export type LinkFailureKind =
  "expired" | "used" | "invalid" | "missing" | "sign_in_failed" | "other";

/** The verify page's screens. */
export type VerifyScreen = "look_up" | "code" | "pending" | "refused";

/** What the verify page started from: a grant handed over in router state,
 * or one to mint for the submission in the URL. */
export type VerifyEntryKind = "grant" | "mint";

export type SignedInConfirmOutcome =
  "confirmed" | "refused" | "pending" | "unavailable";

export type PendingOutcome = "retrying" | "confirmed" | "exhausted";

/** The screens that offer a way back to a typed email or phone. */
export type UseDifferentSurface = "verify" | "reentry" | "start" | "account";

/** The use-different surface a hosting screen reports as. */
export function differentSurfaceOf(
  surface: ConfirmSurface,
): UseDifferentSurface {
  switch (surface) {
    case "verify":
    case "verify_look_up":
      return "verify";
    case "reentry":
      return "reentry";
    case "account":
      return "account";
    case "start_panel":
    case "login":
      return "start";
  }
}

/** The screen a call button sits on. */
export type CallScreen =
  | "code"
  | "session_ended"
  | "unreachable"
  | "prompt"
  | "refused_send"
  | "look_up"
  | "refused"
  | "pending"
  | "reentry"
  | "sign_in"
  | "invite"
  | "link_error";

/** Where a fix of the typed destination happens and what it runs on: the
 * hosting screen, the destination's channel, and the proof the visitor
 * arrived with. */
export type FixContext = {
  surface: ConfirmSurface;
  channel: DestinationChannel;
  proof: "arrival" | "zip";
};

/** Why a fix was refused: the server's refusal, or the client's own check
 * of what the visitor typed. */
export type FixRefusalReason = CorrectionRefusalReason | "invalid_input";

/** What the server's answer to the inline sign-in or quote lookup came to. */
export type SignInResult =
  | { result: "code_sent" | "create_account" | "other_channel" }
  | { result: "failed"; reason: SignInFailure };

function event(name: string, properties: AnalyticsProperties = {}) {
  return { name, properties };
}

function fixProperties(fix: FixContext): AnalyticsProperties {
  return { surface: fix.surface, channel: fix.channel, proof: fix.proof };
}

export const leadFlowEvents = {
  started: (): LeadFlowEvent => event("lead_flow_started"),

  stepViewed: (view: {
    step: QuestionStepId;
    entry: StepEntry;
    index: number;
    total: number;
  }): LeadFlowEvent =>
    event("lead_flow_step_viewed", {
      step: view.step,
      entry: view.entry,
      step_index: view.index,
      step_total: view.total,
    }),

  stepCompleted: (step: QuestionStepId): LeadFlowEvent =>
    event("lead_flow_step_completed", { step }),

  stepInvalid: (step: QuestionStepId): LeadFlowEvent =>
    event("lead_flow_step_invalid", { step }),

  outOfArea: (atNeed: boolean): LeadFlowEvent =>
    event("lead_flow_out_of_area", { at_need: atNeed }),

  endViewed: (screen: EndScreen): LeadFlowEvent =>
    event("lead_flow_end_viewed", { screen }),

  submitted: (
    outcome: SubmitOutcome,
    reason?: SubmitFailureReason,
  ): LeadFlowEvent =>
    event("lead_flow_submitted", reason ? { outcome, reason } : { outcome }),

  confirmPromptShown: (): LeadFlowEvent =>
    event("lead_flow_confirm_prompt_shown"),

  confirmViewed: (view: {
    purpose: string;
    surface: ConfirmSurface;
    method: ConfirmSendMethod;
    resumed: boolean;
  }): LeadFlowEvent =>
    event("lead_flow_confirm_viewed", {
      purpose: view.purpose,
      surface: view.surface,
      method: view.method,
      resumed: view.resumed,
    }),

  confirmStarted: (send: {
    purpose: string;
    surface: ConfirmSurface;
    method: ConfirmSendMethod;
    trigger: ConfirmTrigger;
    sendCount: number;
  }): LeadFlowEvent =>
    event("lead_flow_confirm_started", {
      purpose: send.purpose,
      surface: send.surface,
      method: send.method,
      trigger: send.trigger,
      send_count: send.sendCount,
    }),

  confirmFailed: (failure: {
    purpose: string;
    surface: ConfirmSurface;
    method: ConfirmMethod;
    reason: ConfirmFailureReason;
  }): LeadFlowEvent =>
    event("lead_flow_confirm_failed", {
      purpose: failure.purpose,
      surface: failure.surface,
      method: failure.method,
      reason: failure.reason,
    }),

  confirmAccepted: (proof: {
    purpose: string;
    surface: ConfirmSurface;
    method: ConfirmMethod;
  }): LeadFlowEvent =>
    event("lead_flow_confirm_accepted", {
      purpose: proof.purpose,
      surface: proof.surface,
      method: proof.method,
    }),

  confirmRetryClicked: (retry: {
    surface: ConfirmSurface;
    method: ConfirmSendMethod;
  }): LeadFlowEvent =>
    event("lead_flow_confirm_retry_clicked", {
      surface: retry.surface,
      method: retry.method,
    }),

  sessionEndedViewed: (view: {
    purpose: EventPurpose;
    surface: ConfirmSurface;
    stage: SessionEndedStage;
    resolution: SessionEndedResolution;
  }): LeadFlowEvent =>
    event("lead_flow_session_ended_viewed", {
      purpose: view.purpose,
      surface: view.surface,
      stage: view.stage,
      resolution: view.resolution,
    }),

  sessionRenewal: (renewal: {
    purpose: EventPurpose;
    outcome: SessionRenewalOutcome;
  }): LeadFlowEvent =>
    event("lead_flow_session_renewal", {
      purpose: renewal.purpose,
      outcome: renewal.outcome,
    }),

  linkOpened: (link: { purpose: EventPurpose }): LeadFlowEvent =>
    event("lead_flow_link_opened", { purpose: link.purpose }),

  linkFailed: (link: {
    purpose: EventPurpose;
    kind: LinkFailureKind;
  }): LeadFlowEvent =>
    event("lead_flow_link_failed", { purpose: link.purpose, kind: link.kind }),

  verifyViewed: (view: {
    screen: VerifyScreen;
    entry: VerifyEntryKind;
  }): LeadFlowEvent =>
    event("lead_flow_verify_viewed", {
      screen: view.screen,
      entry: view.entry,
    }),

  verifySignedInConfirm: (
    outcome: SignedInConfirmOutcome,
    reason?: ConfirmRefusal,
  ): LeadFlowEvent =>
    event(
      "lead_flow_verify_signed_in_confirm",
      reason === undefined ? { outcome } : { outcome, reason },
    ),

  verifyPending: (pending: {
    attempt: number;
    outcome: PendingOutcome;
  }): LeadFlowEvent =>
    event("lead_flow_verify_pending", {
      attempt: pending.attempt,
      outcome: pending.outcome,
    }),

  useDifferentClicked: (surface: UseDifferentSurface): LeadFlowEvent =>
    event("lead_flow_use_different_clicked", { surface }),

  callClicked: (call: {
    surface: ConfirmSurface;
    screen: CallScreen;
  }): LeadFlowEvent =>
    event("lead_flow_call_clicked", {
      surface: call.surface,
      screen: call.screen,
    }),

  signInStarted: (mode: SignInMode, surface: SignInSurface): LeadFlowEvent =>
    event("lead_flow_sign_in_started", { mode, surface }),

  signInResult: (
    mode: SignInMode,
    surface: SignInSurface,
    answer: SignInResult,
  ): LeadFlowEvent =>
    event("lead_flow_sign_in_result", { mode, surface, ...answer }),

  verifyRefused: (reason: ConfirmRefusal): LeadFlowEvent =>
    event("lead_flow_verify_refused", { reason }),

  accountCreated: (mode: SignInMode): LeadFlowEvent =>
    event("lead_flow_account_created", { mode }),

  confirmCompleted: (outcome: ConfirmCompletedOutcome): LeadFlowEvent =>
    event("lead_flow_confirm_completed", { outcome }),

  fixOpened: (fix: FixContext): LeadFlowEvent =>
    event("lead_flow_fix_opened", fixProperties(fix)),

  fixSaved: (fix: FixContext, outcome: CorrectionSaveOutcome): LeadFlowEvent =>
    event("lead_flow_fix_saved", { ...fixProperties(fix), outcome }),

  fixRefused: (fix: FixContext, reason: FixRefusalReason): LeadFlowEvent =>
    event("lead_flow_fix_refused", { ...fixProperties(fix), reason }),

  reminderLinkOpened: (): LeadFlowEvent =>
    event("lead_flow_reminder_link_opened"),
};
